import { environment } from '../environments/environment';

/**
 * Tenant capabilities that used to be `environment.name === '<Tenant>'` comparisons. Those only
 * worked while every tenant had its own build - one image serving all of them resolves a single
 * constant name, so every such check would answer for the wrong tenant.
 *
 * The values come from the resolved tenant's TenantConfig.FeatureFlags, which TenantConfigService
 * merges into `environment` before the app renders. Every flag is unset (falsy) for a tenant that
 * hasn't been configured, which matches how tenants outside the old hardcoded comparisons already
 * behaved - so the fallback is the pre-existing behaviour, not a new default.
 *
 * Named for the capability rather than the tenant, since these are meant to outlive "who asked for
 * it first". `standardCharteredUnderwriting` keeps its existing name; it is already stored against
 * live tenants and renaming it would silently strip the behaviour.
 *
 * This list is the single source of truth: the FeatureFlag type is derived from it, and the admin
 * Portals tab builds its toggles from it. That coupling is deliberate - the admin form saves the
 * whole flag object, so a flag missing from the form is a flag wiped from the tenant the next time
 * anyone presses Save.
 */
export const FEATURE_FLAGS = [
  {
    key: 'v2Storefront',
    label: 'Modern storefront (v2)',
    description: 'Product-tile landing page instead of the legacy buy-cover flow.',
  },
  {
    key: 'v2Dashboard',
    label: 'Modern dashboard (v2)',
    description: 'New policy/claims dashboard layout after login.',
  },
  {
    key: 'standardCharteredUnderwriting',
    label: 'Standard Chartered underwriting flow',
    description: 'Single-product motor journey with its own landing copy.',
  },
  {
    key: 'partnerWallet',
    label: 'Partner wallet payments',
    description: "Pay from the partner's own wallet instead of the usual intermediary methods.",
  },
  {
    key: 'embeddedPartnerSession',
    label: 'Embedded partner session',
    description:
      "Portal opens from inside the partner's app with the client already identified: the insured is prefilled and Log Out is hidden.",
  },
] as const;

export type FeatureFlag = (typeof FEATURE_FLAGS)[number]['key'];

export function hasFeature(flag: FeatureFlag): boolean {
  return environment.featureFlags?.[flag] === true;
}
